import { useEffect, useRef } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Inbox } from "lucide-react";
import moment from "moment-timezone";
import Avatar from "../../../shared/Avatar";
import useApi from "../../../../utils/api";
import { renderUserName } from "../../../../utils/helpers/user";

const PER_PAGE = 20;

function Notes({ user }: any) {
    const { t } = useTranslation();
    const { authGet } = useApi();
    const sentinelRef = useRef<HTMLDivElement>(null);

    const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery({
        queryKey: [`/note/${user?.id}`],
        queryFn: ({ pageParam }) =>
            authGet(`/note/${user?.id}`, { params: { page: pageParam, perPage: PER_PAGE } }),
        initialPageParam: 1,
        getNextPageParam: (lastPage: any) => {
            const result = lastPage?.result ?? [];
            const loaded = ((lastPage?.page ?? 1) - 1) * PER_PAGE + result.length;
            return loaded < (lastPage?.totalItems ?? 0) ? (lastPage?.page ?? 1) + 1 : undefined;
        },
        enabled: !!user?.id,
    });

    useEffect(() => {
        const el = sentinelRef.current;
        if (!el) return;

        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
                fetchNextPage();
            }
        });

        observer.observe(el);
        return () => observer.disconnect();
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    const notes: any[] = data?.pages?.flatMap((p: any) => p?.result ?? []) ?? [];
    const totalItems: number | undefined = data?.pages?.[0]?.totalItems;

    if (isLoading) {
        return (
            <div className="tw-border tw-rounded-xl tw-overflow-hidden">
                <ul className="tw-divide-y">
                    {Array.from({ length: 5 }).map((_, i) => (
                        <li key={i} className="tw-flex tw-items-start tw-gap-3 tw-px-5 tw-py-4">
                            <div className="tw-h-8 tw-w-8 tw-rounded-full tw-bg-muted tw-animate-pulse tw-shrink-0 tw-mt-0.5" />
                            <div className="tw-flex-1 tw-space-y-2 tw-pt-0.5">
                                <div className="tw-h-3 tw-w-28 tw-rounded tw-bg-muted tw-animate-pulse" />
                                <div className="tw-h-3 tw-w-full tw-rounded tw-bg-muted tw-animate-pulse" />
                                <div className="tw-h-3 tw-w-3/4 tw-rounded tw-bg-muted tw-animate-pulse" />
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
        );
    }

    if (notes.length === 0) {
        return (
            <div className="tw-flex tw-flex-col tw-text-neutral-500 tw-justify-center tw-items-center tw-py-8 tw-border tw-border-dashed tw-rounded-lg">
                <Inbox />
                <p className="tw-text-muted-foreground">{t("no-data")}</p>
            </div>
        );
    }

    return (
        <div className="tw-border tw-rounded-xl tw-overflow-hidden">
            {totalItems != null && (
                <div className="tw-px-5 tw-py-3 tw-border-b tw-flex tw-items-center tw-gap-2">
                    <span className="tw-text-sm tw-font-medium">{t("notes")}</span>
                    <span className="tw-text-xs tw-bg-muted tw-text-muted-foreground tw-rounded-full tw-px-2 tw-py-0.5 tw-font-medium">
                        {totalItems}
                    </span>
                </div>
            )}
            <ul className="tw-divide-y">
                {notes.map((note: any) => (
                    <li key={note.id} className="tw-flex tw-items-start tw-gap-3 tw-px-5 tw-py-4 hover:tw-bg-muted/30 tw-transition-colors">
                        <Avatar user={note.user} size="sm" className="tw-mt-0.5 tw-shrink-0" />
                        <div className="tw-min-w-0 tw-flex-1">
                            <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-mb-1.5">
                                <p className="tw-text-sm tw-font-medium tw-leading-none">
                                    {renderUserName(note.user)}
                                </p>
                                {note.createdAt && (
                                    <span className="tw-text-xs tw-text-muted-foreground tw-shrink-0">
                                        {moment(note.createdAt).fromNow()}
                                    </span>
                                )}
                            </div>
                            <p className="tw-text-sm tw-text-muted-foreground tw-whitespace-pre-wrap tw-break-words tw-leading-relaxed">
                                {note.value}
                            </p>
                        </div>
                    </li>
                ))}
            </ul>

            <div ref={sentinelRef} className="tw-py-3 tw-flex tw-items-center tw-justify-center">
                {(isFetchingNextPage || hasNextPage) && (
                    <div className="tw-h-4 tw-w-4 tw-animate-spin tw-rounded-full tw-border-2 tw-border-primary tw-border-t-transparent" />
                )}
            </div>
        </div>
    );
}

export default Notes;
